import React from "react";

import { useNavigate } from "react-router-dom";

import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import {
  getPersonalizedFeaturedGames,
  trackGameView,
} from "../utils/sportsPreferences";

const FeaturedGames = ({ games }) => {
  const navigate = useNavigate();

  if (!games || games.length === 0) {
    return null;
  }

  // Get personalized featured games based on user preferences
  const featuredGames = getPersonalizedFeaturedGames(games, 4);

  const handleGameClick = (game) => {
    // Track this view for personalization
    trackGameView(game);

    // Navigate to event detail page with complete event data
    const eventId = game.id || game.mediaId;

    // Reconstruct event object with all necessary fields for detail page
    const eventData = {
      id: game.id,
      mediaId: game.mediaId,
      name: game.name || game.title,
      sport: game.sport,
      strHomeTeam: game.homeTeam,
      strAwayTeam: game.awayTeam,
      strHomeTeamBadge: game.homeTeamLogo,
      strAwayTeamBadge: game.awayTeamLogo,
      intHomeScore: game.homeTeamScore,
      intAwayScore: game.awayTeamScore,
      homeTeamScore: game.homeTeamScore,
      awayTeamScore: game.awayTeamScore,
      strLeague: game.league,
      strLeagueBadge: game.leagueLogo,
      strVenue: game.venue,
      strThumb: game.backdrop,
      backdrop: game.backdrop,
      posterPath: game.poster,
      thumbnail: game.poster,
      strSport: game.genre,
      categoryName: game.genre,
      timestamp: game.timestamp,
      strTimestamp: game.timestamp,
    };

    navigate(`/web-player/sports/event/${eventId}`, {
      state: { event: eventData },
    });
  };

  return (
    <div className="px-24 mb-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-white">Featured Games</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {featuredGames.map((game, index) => {
          // Determine if game is live
          const isLive = game.rating === "LIVE";

          return (
            <div
              className="group relative bg-white/5 backdrop-blur-md rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300 cursor-pointer hover:scale-105"
              key={game.id || index}
              role="button"
              tabIndex={0}
              onClick={() => handleGameClick(game)}
              onKeyDown={activateOnEnterOrSpace}
            >
              {/* Background Image */}
              {game.poster && (
                <div className="absolute inset-0">
                  <img
                    alt={game.title}
                    className="w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity"
                    src={game.poster}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                </div>
              )}

              {/* Content */}
              <div className="relative z-10 p-4">
                {/* LIVE Badge */}
                {isLive && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold uppercase animate-pulse">
                      LIVE
                    </span>
                  </div>
                )}

                {/* League/Sport */}
                <div className="flex items-center gap-2 mb-3">
                  {game.leagueLogo && (
                    <img
                      alt={game.league}
                      className="w-5 h-5 object-contain"
                      src={game.leagueLogo}
                    />
                  )}
                  <span className="text-white/70 text-xs font-semibold uppercase">
                    {game.league || game.genre}
                  </span>
                </div>

                {/* Teams */}
                <div className="space-y-2 mb-3">
                  {/* Away Team */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {game.awayTeamLogo && (
                        <img
                          alt={game.awayTeam}
                          className="w-6 h-6 object-contain flex-shrink-0 team-logo"
                          src={game.awayTeamLogo}
                        />
                      )}
                      <span className="text-white text-sm font-medium truncate">
                        {game.awayTeam}
                      </span>
                    </div>
                    <span className="text-white text-lg font-bold ml-2">
                      {game.awayTeamScore ?? 0}
                    </span>
                  </div>

                  {/* Home Team */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {game.homeTeamLogo && (
                        <img
                          alt={game.homeTeam}
                          className="w-6 h-6 object-contain flex-shrink-0 team-logo"
                          src={game.homeTeamLogo}
                        />
                      )}
                      <span className="text-white text-sm font-medium truncate">
                        {game.homeTeam}
                      </span>
                    </div>
                    <span className="text-white text-lg font-bold ml-2">
                      {game.homeTeamScore ?? 0}
                    </span>
                  </div>
                </div>

                {/* Venue */}
                {game.venue && (
                  <div className="text-white/60 text-xs truncate">
                    {game.venue}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeaturedGames;
